import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Menu, MenuItem } from '@material-ui/core';
import ItemSourceGuesserItemsModel from 'models/ItemSourceGuesserItemsModel';
import { ItemSourceGuesserModel } from 'models/ItemSourceGuesserModel';
import { IItemSourceGuesserItem } from 'types/itemSourceGuesser';
import { ItemSourceGuesserItemStatus as ItemStatus } from 'constants/itemSourceGuesserStatus';

interface Props {
  itemsModel: ItemSourceGuesserItemsModel;
  model: ItemSourceGuesserModel;
  onActionComplete: () => void;
}

const ItemSourceGuesserBulkActions: React.FC<Props> = ({ itemsModel, model, onActionComplete }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  const { countChange, countAccept, countReview } = useMemo(() => {
    if (!itemsModel.gridData || !Array.isArray(itemsModel.gridData))
      return { countChange: 0, countAccept: 0, countReview: 0 };
    const items = itemsModel.gridData as IItemSourceGuesserItem[];
    let change = 0,
      accept = 0,
      review = 0;
    items.forEach((it) => {
      if (it.status === ItemStatus.NEEDS_USER_REVIEW) {
        review++;
        // Heuristic: if original source empty, treat as accept candidates
        if (!it.item.source || it.item.source.trim() === '') accept++;
        else change++;
      }
    });
    return { countChange: change, countAccept: accept, countReview: review };
  }, [itemsModel.gridData]);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget);
  const closeMenu = () => setAnchor(null);

  const doBulk = async (action: 'keep' | 'change' | 'accept' | 'acceptHigh') => {
    closeMenu();
    if (busy) return;
    setBusy(true);
    try {
      if (action === 'keep') await itemsModel.bulkKeep();
      else if (action === 'change') await itemsModel.bulkChange();
      else if (action === 'accept') await itemsModel.bulkAccept();
      else await itemsModel.bulkAcceptHighConfidence();
      await model.reload();
      onActionComplete();
    } catch (e) {
      console.error('Bulk action failed', e);
    } finally {
      setBusy(false);
    }
  };

  if (countReview === 0) return null;

  return (
    <>
      <BulkActionsButton onClick={openMenu} disabled={busy || itemsModel.loadingGrid}>
        {busy ? 'Processing...' : 'Bulk Actions'}
      </BulkActionsButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
        <MenuItem onClick={() => doBulk('keep')} disabled={countReview === 0}>
          Keep All Needs Review ({countReview})
        </MenuItem>
        <MenuItem onClick={() => doBulk('change')} disabled={countChange === 0}>
          Apply All Changes ({countChange})
        </MenuItem>
        <MenuItem onClick={() => doBulk('accept')} disabled={countAccept === 0}>
          Accept All ({countAccept})
        </MenuItem>
        <MenuItem onClick={() => doBulk('acceptHigh')} disabled={countAccept === 0}>
          Accept High Confidence
        </MenuItem>
      </Menu>
    </>
  );
};

export default ItemSourceGuesserBulkActions;

const BulkActionsButton = styled.button`
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;
  &:hover:not(:disabled) {
    background-color: #45a049;
  }
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
